import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TONES, GOALS } from "@/lib/generateConstants";

export function BulkSharedInputs({
  topic, setTopic, researchText, setResearchText,
  audience, setAudience, goal, setGoal,
  cta, setCta, aiModel, setAiModel, availableModels,
  toneVariations, toggleTone,
}) {
  return (
    <div className="space-y-4">
      <div>
        <label className="block font-['JetBrains_Mono'] text-xs uppercase tracking-[0.2em] text-[var(--text-muted)] mb-2">
          Topic <span className="text-[#FF4400]">*</span>
        </label>
        <Input
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          placeholder="The central theme for all content pieces"
          maxLength={255}
          className="bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:ring-1 focus:ring-[#FF4400] h-11"
          data-testid="bulk-topic-input"
        />
      </div>

      <div>
        <label className="block font-['JetBrains_Mono'] text-xs uppercase tracking-[0.2em] text-[var(--text-muted)] mb-2">
          Research / Source Material <span className="text-[#FF4400]">*</span>
        </label>
        <Textarea
          value={researchText}
          onChange={(e) => setResearchText(e.target.value)}
          placeholder="Shared research context for all pieces..."
          maxLength={50000}
          className="bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:ring-1 focus:ring-[#FF4400] min-h-[120px] resize-y"
          data-testid="bulk-research-input"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block font-['JetBrains_Mono'] text-xs uppercase tracking-[0.2em] text-[var(--text-muted)] mb-2">
            Audience <span className="text-[#FF4400]">*</span>
          </label>
          <Input
            value={audience}
            onChange={(e) => setAudience(e.target.value)}
            placeholder="e.g., B2B SaaS founders"
            className="bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:ring-1 focus:ring-[#FF4400] h-11"
            data-testid="bulk-audience-input"
          />
        </div>
        <div>
          <label className="block font-['JetBrains_Mono'] text-xs uppercase tracking-[0.2em] text-[var(--text-muted)] mb-2">
            Goal <span className="text-[#FF4400]">*</span>
          </label>
          <Select value={goal} onValueChange={setGoal}>
            <SelectTrigger className="bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-primary)] focus:ring-1 focus:ring-[#FF4400] h-11" data-testid="bulk-goal-select">
              <SelectValue placeholder="Select goal" />
            </SelectTrigger>
            <SelectContent className="bg-[var(--bg-raised)] border-[var(--border-strong)]">
              {GOALS.map((g) => (
                <SelectItem key={g} value={g} className="text-[var(--text-secondary)] focus:bg-[var(--accent-subtle)] focus:text-white">{g}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block font-['JetBrains_Mono'] text-xs uppercase tracking-[0.2em] text-[var(--text-muted)] mb-2">
            CTA (Optional)
          </label>
          <Input
            value={cta}
            onChange={(e) => setCta(e.target.value)}
            placeholder="e.g., Book a call"
            className="bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:ring-1 focus:ring-[#FF4400] h-11"
            data-testid="bulk-cta-input"
          />
        </div>
        <div>
          <label className="block font-['JetBrains_Mono'] text-xs uppercase tracking-[0.2em] text-[var(--text-muted)] mb-2">
            AI Model <span className="text-[#FF4400]">*</span>
          </label>
          <Select value={aiModel} onValueChange={setAiModel}>
            <SelectTrigger className="bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-primary)] focus:ring-1 focus:ring-[#FF4400] h-11" data-testid="bulk-model-select">
              <SelectValue placeholder="Select model" />
            </SelectTrigger>
            <SelectContent className="bg-[var(--bg-raised)] border-[var(--border-strong)]">
              {availableModels.map((m) => (
                <SelectItem key={m} value={m} className="text-[var(--text-secondary)] focus:bg-[var(--accent-subtle)] focus:text-white">{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <label className="block font-['JetBrains_Mono'] text-xs uppercase tracking-[0.2em] text-[var(--text-muted)] mb-2">
          Tone <span className="text-[#FF4400]">*</span>
        </label>
        <div className="flex flex-wrap gap-2">
          {TONES.map((tone) => (
            <button
              key={tone}
              type="button"
              onClick={() => toggleTone(tone)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-200 ${
                toneVariations.includes(tone)
                  ? "bg-[var(--accent-subtle)] border-[#FF4400] text-[#FF4400]"
                  : "bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-secondary)] hover:border-[var(--border-strong)]"
              }`}
              data-testid={`bulk-tone-${tone.toLowerCase()}`}
            >
              {tone}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
